import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

/**
 * Single component for /payment/result (Transbank Webpay callback).
 * Reads ?status=success|failed|aborted&ref=... from the query string,
 * calls /api/payments/confirm/{ref} to sync local state, then either
 * bounces to the dashboard or shows a retry CTA.
 */
export default function PaymentResult({ outcome }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  // Prefer the ref from the URL; fall back to what Onboarding stored.
  const ref = params.get("ref") ||
    params.get("external_reference") ||
    (typeof window !== "undefined" && sessionStorage.getItem("tbk_external_reference"));

  // Transbank return URL sends ?status=success|failed|aborted — normalize it
  // into the outcome the page shows so we don't wait for the confirm call
  // when the transaction was clearly aborted.
  const urlStatus = params.get("status");
  const effectiveOutcome = urlStatus === "aborted" ? "failure"
    : urlStatus === "failed" ? "failure"
    : urlStatus === "success" ? "success"
    : outcome;

  useEffect(() => {
    let cancelled = false;
    async function syncAndDispatch() {
      if (!ref) { setStatus(effectiveOutcome === "success" ? "approved" : effectiveOutcome); return; }
      try {
        const { data } = await api.get(`/payments/confirm/${encodeURIComponent(ref)}`);
        if (cancelled) return;
        setInfo(data);
        setStatus(data.status);
        if (data.paid) {
          await refresh();
          try { sessionStorage.removeItem("tbk_external_reference"); } catch (_e) { /* ignore */ }
          track("purchase", {
            currency: "CLP",
            value: data.amount || 39990,
            plan: "borrame",
            method: "transbank",
          });
          // Kick off the ARCOP dispatch once payment is confirmed.
          try {
            await api.post("/requests/dispatch");
            track("arcop_dispatched", { source: "post_payment" });
          } catch (_e) {
            // Non-fatal — user can trigger from dashboard manually.
          }
          toast.success("Pago aprobado. Generando solicitudes ARCOP…");
          setTimeout(() => navigate("/dashboard"), 1400);
        }
      } catch (_err) {
        if (!cancelled) {
          setStatus(effectiveOutcome === "success" ? "error" : effectiveOutcome);
          track("purchase_failed", { outcome: effectiveOutcome });
        }
      }
    }
    syncAndDispatch();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const isApproved = status === "approved" || (outcome === "success" && info?.subscribed);
  const isPending  = status === "pending"  || status === "in_process" || outcome === "pending";
  const isFailed   = status === "rejected" || status === "cancelled" || status === "error" || outcome === "failure";

  return (
    <div className="bg-[#F9F9F8] min-h-[calc(100vh-160px)] flex items-center" data-testid="payment-result">
      <div className="max-w-xl mx-auto px-6 py-20 w-full">
        <div className="bg-white border border-gray-200 rounded-none p-10 shadow-[0_4px_0_0_rgba(15,23,42,0.04)]">
          {isApproved && (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mb-5" strokeWidth={1.3} />
              <h1 className="font-display text-3xl font-bold text-gray-900">Pago aprobado</h1>
              <p className="mt-3 text-gray-600">
                Tu suscripción a borra-me.com está activa. Estamos generando las
                solicitudes ARCOP para cada broker detectado.
              </p>
            </>
          )}
          {isPending && !isApproved && (
            <>
              <Clock className="w-14 h-14 text-amber-600 mb-5" strokeWidth={1.3} />
              <h1 className="font-display text-3xl font-bold text-gray-900">Pago en proceso</h1>
              <p className="mt-3 text-gray-600">
                Webpay está validando el pago. Te avisaremos en cuanto se
                confirme — normalmente menos de 10 minutos.
              </p>
            </>
          )}
          {isFailed && (
            <>
              <XCircle className="w-14 h-14 text-red-600 mb-5" strokeWidth={1.3} />
              <h1 className="font-display text-3xl font-bold text-gray-900">No se completó el pago</h1>
              <p className="mt-3 text-gray-600">
                No se pudo procesar el cobro. Puedes volver a intentar o cambiar
                de plan desde el onboarding.
              </p>
            </>
          )}
          {status === "checking" && (
            <div className="flex items-center gap-3 text-gray-700">
              <Loader2 className="w-5 h-5 animate-spin" />
              Confirmando con Webpay Transbank…
            </div>
          )}

          {info && (
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm border-t border-gray-200 pt-5">
              <div>
                <div className="eyebrow mb-1">Plan</div>
                <div className="font-semibold text-gray-900 capitalize">{info.plan}</div>
              </div>
              <div>
                <div className="eyebrow mb-1">Modalidad</div>
                <div className="font-semibold text-gray-900">Pago único</div>
              </div>
              <div>
                <div className="eyebrow mb-1">Monto</div>
                <div className="font-semibold text-gray-900">${info.amount?.toLocaleString("es-CL")} CLP</div>
              </div>
              <div>
                <div className="eyebrow mb-1">Estado</div>
                <div className="font-semibold text-gray-900 uppercase tracking-wider text-xs">{info.status}</div>
              </div>
            </div>
          )}

          <div className="mt-8 flex gap-3">
            {isApproved ? (
              <button onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard"
                className="bg-[#0F172A] hover:bg-black text-white px-6 py-3 text-sm font-semibold">
                Ir al Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate("/onboarding")} data-testid="retry-payment"
                  className="bg-[#0F172A] hover:bg-black text-white px-6 py-3 text-sm font-semibold">
                  Reintentar
                </button>
                <button onClick={() => navigate("/")} className="border border-gray-300 text-gray-700 px-6 py-3 text-sm font-semibold hover:bg-gray-50">
                  Volver al inicio
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
